import React from "react";
import { Text } from "@chakra-ui/react";
import { Section, sectionSpacingY } from "components/section";
import { Bullets } from "components/bullets";
import { Link } from "ui/link";
import { PairImage } from "components/pair-image";
import pair1Image from "../../../../public/assets/platform/security/security-pairs/pair-1.png";
import pair2Image from "../../../../public/assets/platform/security/security-pairs/pair-2.png";
import pair3Image from "../../../../public/assets/platform/security/security-pairs/pair-3.svg";

function SecurityPairs() {
  return (
    <>
      <Section>
        <PairImage
          image={pair1Image}
          align="left"
          eyebrow="Your data in your warehouse"
          title="Novel hybrid architecture"
          actionText="Learn more"
          actionUrl="/docs/security/overview#basic-architecture-overview"
          description={
            <>
              <Text>
                Hightouch never stores any of your data. We believe you should maintain control and take advantage of
                your existing architecture. Here’s how your data passes through Hightouch:
              </Text>
              <Bullets
                spacing="smaller"
                mt="smaller"
                textAlign="left"
                items={[
                  "Hightouch runs queries directly on your warehouse.",
                  "You can choose to store all customer data temporarily in your cloud.",
                  "Hightouch transfers data to your destination without ever storing it.",
                  <Text key="aws-private-link">
                    Hightouch also supports{" "}
                    <Link variant="article" href="https://hightouch.com/docs/security/aws-privatelink">
                      AWS PrivateLink
                    </Link>{" "}
                    for Snowflake in certain regions.
                  </Text>,
                ]}
              />
            </>
          }
        />
      </Section>

      <Section>
        <PairImage
          image={pair2Image}
          align="right"
          eyebrow="Control access on your own terms"
          title="Enterprise-ready on day one"
          actionText="Learn more"
          actionUrl="/blog/enterprise-ready-on-day-1"
          description={
            <>
              <Text>
                Hightouch provides the control and governance capabilities you need to safely plug Reverse ETL into your
                tech stack.
              </Text>
              <Bullets
                spacing="smaller"
                textAlign="left"
                mt="smaller"
                items={[
                  <Text key="SSO">
                    <Text as="strong" fontWeight="bold">
                      SSO:{" "}
                    </Text>
                    Manage user authentication centrally with SAML SSO and keep access grants up-to-date with support
                    for SCIM.
                  </Text>,
                  <Text key="Role-based access">
                    <Text as="strong" fontWeight="bold">
                      Role-based access:{" "}
                    </Text>
                    Leverage fine-grained permissions and custom roles to control who has access to specific workspace
                    resources (models, destinations, and syncs).
                  </Text>,
                  <Text key="Row-level access">
                    <Text as="strong" fontWeight="bold">
                      Row-level access:{" "}
                    </Text>
                    Grant users access to data based on geography, business unit, or any other parameter.
                  </Text>,
                  <Text key="Row-level sync controls">
                    <Text as="strong" fontWeight="bold">
                      Row-level sync controls:{" "}
                    </Text>
                    Control which data can be synced to each of your destinations, to ensure data only travels to
                    approved tools.
                  </Text>,
                  <Text key="Audit logs">
                    <Text as="strong" fontWeight="bold">
                      Audit logs:{" "}
                    </Text>
                    Track, monitor, and search all in-app user activity. Export reports and alerts to your preferred
                    tools.
                  </Text>,
                  <Text key="Approval flows">
                    <Text as="strong" fontWeight="bold">
                      Approval flows:{" "}
                    </Text>
                    Require users to receive in-app approval before pushing changes into production. Rollback unintended
                    changes immediately through Git.
                  </Text>,
                ]}
              />
            </>
          }
        />
      </Section>

      <Section my={0} pb={sectionSpacingY}>
        <PairImage
          image={pair3Image}
          align="left"
          eyebrow="We follow industry security standards"
          title="Product security"
          actionText="Learn more"
          actionUrl="/docs/security/overview"
          description={
            <>
              <Text>
                Our platform architecture and industry compliance certifications mean that your data is always secure.
              </Text>

              <Bullets
                spacing="smaller"
                mt="smaller"
                textAlign="left"
                items={[
                  "We complete regular security-design reviews, pen tests using trusted security vendors, and conduct regular audits such as SOC 2 Type 2.",
                  "We encrypt all data at rest and protect it in transit with TLS. Hightouch’s metadata database is encrypted by Amazon AWS using standard AES-256 encryption algorithms.",
                  "We limit internal access to tools and resources using time-based access.",
                ]}
              />
            </>
          }
        />
      </Section>
    </>
  );
}

export default SecurityPairs;
